import type { NoiseSuppressorParams, RNNoiseInstance } from '../../../../../types/streamEffects';

const BYTES_PER_FLOAT32 = 4;

export default class NoiseSuppressionScriptProcessor {
  readonly #instance: RNNoiseInstance;

  readonly #context: AudioContext;

  readonly #circularBuffer: Float32Array;

  readonly #state: number;

  readonly #processor: ScriptProcessorNode;

  #alive = true;

  constructor(params: NoiseSuppressorParams) {
    this.#instance = params.noiseSuppressor.instance.exports as unknown as RNNoiseInstance;
    this.#context = params.context;
    this.#circularBuffer = new Float32Array(this.#instance.memory.buffer);
    this.#state = this.#instance.newState();
    this.#processor = this.#context.createScriptProcessor();
    this.setOnAudioProcess();
  }

  get noiseSuppressionScriptProcessorNode(): ScriptProcessorNode {
    return this.#processor;
  }

  update(keepalive: boolean): void {
    if (!keepalive) {
      this.#alive = false;
      this.#instance.deleteState(this.#state);
    }

    const e = Object.assign(new Event('status'), { vadProb: this.#instance.getVadProb(this.#state) });
    this.#processor.dispatchEvent(e);
  }

  private setOnAudioProcess(): void {
    this.#processor.addEventListener('audioprocess', ({ inputBuffer, outputBuffer }) => {
      if (!this.#alive) {
        return;
      }

      /*
        - В WebAssembly все адреса считаются в байтах.
        - В JavaScript для работы с тем же куском памяти мы используем Float32Array, где элемент занимает 4 байта.
        - Чтобы из «адреса_в_байтах» → «индекса_в_Float32Array», нужно byteOffset / 4.
        - Аналогично при работе с Int16Array делили бы на 2, с Float64Array — на 8.
        - RNNoise обрабатывает по 10 мс (480 сэмплов при 48 кГц). Если ваш ScriptProcessorNode настроен на 128 сэмплов,
        то только каждое четвёртое срабатывание реально отдаст результат — именно поэтому проверка
        if (pointer) встречается почти на каждом вызове.
        - getVadProb() даёт оценку «есть ли речь», её можно использовать, например, чтобы зажигать индикатор говорящего.
        Так что вся «магия» в двух местах деления на 4 — это простое соответствие между байтовыми адресами WASM и
        индексами Float32Array, а pointer — это адрес начала готового,
        уже очищенного от шума блока в том же кольцевом буфере.
      */
      this.#circularBuffer.set(inputBuffer.getChannelData(0), this.#instance.getInput(this.#state) / BYTES_PER_FLOAT32);
      const output = outputBuffer.getChannelData(0);
      const pointer = this.#instance.pipe(this.#state, output.length) / BYTES_PER_FLOAT32;

      if (pointer) {
        output.set(this.#circularBuffer.subarray(pointer, pointer + output.length));
      }
    });
  }
}
